import React, { useState, useEffect } from 'react';
import { executionPlanApi } from '../services/api';
import socketService from '../services/socket';
import './ExecutionPlanDashboard.css';

/**
 * ExecutionPlanDashboard
 * 
 * Real-time monitoring of execution plan progress
 */
function ExecutionPlanDashboard({ executionId, onClose }) {
    const [execution, setExecution] = useState(null);
    const [goalExecutions, setGoalExecutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadExecution();

        // Subscribe to real-time updates
        socketService.on('execution-plan:update', handleUpdate);

        return () => {
            socketService.off('execution-plan:update', handleUpdate);
        };
    }, [executionId]);

    const loadExecution = async () => {
        try {
            const response = await executionPlanApi.getExecutionStatus(executionId);
            setExecution(response.data.execution);
            setGoalExecutions(response.data.execution.goalExecutions || []);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load execution');
            setLoading(false);
        }
    };

    const handleUpdate = (data) => {
        if (data.planExecutionId !== executionId) return;

        console.log('Execution update:', data);

        switch (data.status) {
            case 'started':
                setExecution(prev => ({
                    ...prev,
                    status: 'running',
                    totalGoals: data.totalGoals
                }));
                break;

            case 'goal_started':
                setGoalExecutions(prev => {
                    const updated = [...prev];
                    const index = updated.findIndex(g => g.testGoalId === data.goalId);
                    if (index >= 0) {
                        updated[index] = {
                            ...updated[index],
                            status: 'running'
                        };
                    }
                    return updated;
                });
                break;

            case 'goal_completed':
                setGoalExecutions(prev => {
                    const updated = [...prev];
                    const index = updated.findIndex(g => g.testGoalId === data.goalId);
                    if (index >= 0) {
                        updated[index] = {
                            ...updated[index],
                            status: data.goalStatus,
                            testExecutionId: data.executionId
                        };
                    }
                    return updated;
                });

                setExecution(prev => ({
                    ...prev,
                    completedGoals: data.progress?.completed || prev.completedGoals + 1
                }));
                break;

            case 'goal_skipped':
                setGoalExecutions(prev => {
                    const updated = [...prev];
                    const index = updated.findIndex(g => g.testGoalId === data.goalId);
                    if (index >= 0) {
                        updated[index] = {
                            ...updated[index],
                            status: 'skipped',
                            errorMessage: data.reason
                        };
                    }
                    return updated;
                });
                break;

            case 'completed':
                setExecution(prev => ({
                    ...prev,
                    status: 'completed',
                    summary: data.summary
                }));
                break;

            case 'error':
                setExecution(prev => ({
                    ...prev,
                    status: 'failed',
                    errorMessage: data.error
                }));
                break;
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel this execution?')) return;

        try {
            await executionPlanApi.cancelExecution(executionId);
        } catch (err) {
            console.error('Failed to cancel execution:', err);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return '⏸️';
            case 'running': return '⏳';
            case 'passed': return '✅';
            case 'failed': return '❌';
            case 'skipped': return '⏭️';
            case 'disabled': return '👁️';
            default: return '⏸️';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'running': return '#2196F3';
            case 'passed': return '#4CAF50';
            case 'failed': return '#F44336';
            case 'skipped': return '#FF9800';
            default: return '#9E9E9E';
        }
    };

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Loading execution...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <p>⚠️ {error}</p>
                <button onClick={onClose}>Close</button>
            </div>
        );
    }

    const progressPercent = execution.totalGoals > 0
        ? Math.round((execution.completedGoals / execution.totalGoals) * 100)
        : 0;

    return (
        <div className="execution-plan-dashboard">
            <div className="dashboard-header">
                <div>
                    <h2>{execution.executionPlan?.name || 'Execution Plan'}</h2>
                    <p className="execution-id">ID: {executionId.slice(0, 8)}...</p>
                </div>
                <div className="header-actions">
                    {execution.status === 'running' && (
                        <button className="cancel-btn" onClick={handleCancel}>
                            Cancel
                        </button>
                    )}
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
            </div>

            {/* Progress Overview */}
            <div className="progress-overview">
                <div className="progress-bar-container">
                    <div
                        className="progress-bar-fill"
                        style={{
                            width: `${progressPercent}%`,
                            background: execution.status === 'failed' ? '#F44336' : '#4CAF50'
                        }}
                    />
                </div>
                <div className="progress-stats">
                    <span>{execution.completedGoals} / {execution.totalGoals} goals</span>
                    <span>{progressPercent}%</span>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card passed">
                    <div className="stat-value">{execution.passedGoals || 0}</div>
                    <div className="stat-label">Passed</div>
                </div>
                <div className="stat-card failed">
                    <div className="stat-value">{execution.failedGoals || 0}</div>
                    <div className="stat-label">Failed</div>
                </div>
                <div className="stat-card skipped">
                    <div className="stat-value">{execution.skippedGoals || 0}</div>
                    <div className="stat-label">Skipped</div>
                </div>
                <div className="stat-card duration">
                    <div className="stat-value">
                        {execution.duration
                            ? `${Math.round(execution.duration / 1000)}s`
                            : execution.status === 'running' ? '⏱️' : '-'
                        }
                    </div>
                    <div className="stat-label">Duration</div>
                </div>
            </div>

            {/* Execution Context */}
            {execution.executionContext && Object.keys(execution.executionContext).length > 0 && (
                <div className="execution-context">
                    <h3>Execution Context</h3>
                    <div className="context-info">
                        {execution.executionContext.auth?.isLoggedIn && (
                            <div className="context-item">
                                ✅ Logged in as {execution.executionContext.auth.currentUser || 'user'}
                            </div>
                        )}
                        {execution.executionContext.navigation?.currentScreen && (
                            <div className="context-item">
                                📍 Current Screen: {execution.executionContext.navigation.currentScreen}
                            </div>
                        )}
                        {execution.executionContext.navigation?.navigationStack?.length > 0 && (
                            <div className="context-item">
                                🗺️ Navigation: {execution.executionContext.navigation.navigationStack.join(' → ')}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Goal List */}
            <div className="goals-execution-list">
                <h3>Goals</h3>
                {goalExecutions.map((goalExec, index) => (
                    <div
                        key={goalExec.id}
                        className={`goal-execution-item ${goalExec.status}`}
                        style={{ borderLeftColor: getStatusColor(goalExec.status) }}
                    >
                        <div className="goal-status-icon">
                            {getStatusIcon(goalExec.status)}
                        </div>
                        <div className="goal-info">
                            <div className="goal-header">
                                <span className="goal-number">#{goalExec.order}</span>
                                <span className="goal-title">{goalExec.testGoal?.goal || 'Goal'}</span>
                            </div>
                            {goalExec.status === 'running' && (
                                <div className="goal-progress">
                                    <div className="pulse-dot"></div>
                                    <span>Executing...</span>
                                </div>
                            )}
                            {goalExec.duration && (
                                <div className="goal-meta">
                                    <span>⏱️ {Math.round(goalExec.duration / 1000)}s</span>
                                    {goalExec.retryCount > 0 && (
                                        <span>🔄 Retry {goalExec.retryCount}</span>
                                    )}
                                </div>
                            )}
                            {goalExec.errorMessage && (
                                <div className="goal-error">
                                    ⚠️ {goalExec.errorMessage}
                                </div>
                            )}
                            {goalExec.testExecutionId && (
                                <a
                                    href={`/executions/${goalExec.testExecutionId}`}
                                    className="view-details-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View Details →
                                </a>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Final Summary */}
            {execution.status === 'completed' && execution.summary && (
                <div className="final-summary">
                    <h3>Execution Summary</h3>
                    <div className="summary-content">
                        <p><strong>Total Duration:</strong> {Math.round(execution.summary.totalDuration / 1000)}s</p>
                        <p><strong>Pass Rate:</strong> {Math.round((execution.summary.passed / execution.summary.total) * 100)}%</p>
                        {execution.summary.goals && (
                            <div className="summary-goals">
                                {execution.summary.goals.map((goal, i) => (
                                    <div key={i} className={`summary-goal ${goal.status}`}>
                                        {getStatusIcon(goal.status)} Goal {i + 1}: {goal.status}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ExecutionPlanDashboard;
